import React, {useCallback} from 'react';
import {
  Button,
  Divider,
  Grid,
  HStack,
  Skeleton,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import {State} from '@synako/halitrek';
import {ItemCatalogue2Desc} from 'api-types/ffconnect-hal';
import {HistoriqueAchatModal} from './HistoriqueAchatModal';
import {useTranslation} from 'react-i18next';
import {HaliRenderOne} from '@synako/haligator';
import {dateAvecSlashes, isItemStateCatalogueFF} from 'ff-common';
import {TimeIcon} from '@chakra-ui/icons';
import {usePeutVoirEvolutionArticle} from '@/hooks/auth/usePermissionsCommerciales';
import {DejaCommandeBadge, DejaCommandeTooltipContent} from './DejaCommande';
import {useArticleDejaCommande} from './useArticleDejaCommande';
import {PastilleEvolution, getStatutEvolutionConfig} from './PastilleEvolution';
import {usePrixHistoriqueArticle} from './usePrixHistoriqueArticle';
import {HistoriqueAchatLigneAvecPrix} from './HistoriqueAchatPrix';

export interface HistoriqueAchatButtonProps {
  itemState: State<ItemCatalogue2Desc> | undefined;
}

const HistoriqueAchatButtonComponent: React.FC<HistoriqueAchatButtonProps> = ({
  itemState,
}) => {
  const {t} = useTranslation();
  // Contrôle manuel pour éviter l'ouverture sur focus automatique.
  const {isOpen, onOpen, onClose} = useDisclosure();
  const peutVoirEvolution = usePeutVoirEvolutionArticle();

  // Article déjà commandé pour la date de livraison sélectionnée (`undefined` si l'article n'est pas FF)
  const code_article_ff =
    itemState && isItemStateCatalogueFF(itemState)
      ? itemState.data.code
      : undefined;
  const {statsDejaCommande, isDejaCommande, date_de_livraison} =
    useArticleDejaCommande(code_article_ff);
  // Prix chargés au survol, quand le tooltip s'ouvre
  const {peutVoirPrix, prixParDate} = usePrixHistoriqueArticle(
    code_article_ff,
    isOpen,
  );

  const handleMouseEnter = useCallback(() => {
    onOpen();
  }, [onOpen]);

  const handleMouseLeave = useCallback(() => {
    onClose();
  }, [onClose]);

  // À l'ouverture de la modale on referme d'abord le tooltip pour éviter qu'il ne reste ouvert derrière.
  const handleClick = useCallback(() => {
    onClose();
    NiceModal.show(HistoriqueAchatModal, {
      itemState,
    });
  }, [itemState, onClose]);

  if (!itemState || !isItemStateCatalogueFF(itemState)) return null;

  const unite = itemState.data.unite_de_commande_client;

  // Badge minimal « déjà commandé seul » : utilisé en fallback quand pas d'historique d'achat.
  const badgeDejaCommandeSeul = isDejaCommande ? (
    <DejaCommandeBadge
      stats={statsDejaCommande}
      unite={unite}
      date={date_de_livraison}
    />
  ) : null;

  // Libellé affiché quand l'article n'a aucun historique d'achat récent.
  const labelAucunHistorique = (
    <Text
      fontSize='xs'
      color='gray.dark'
      fontWeight='500'
      lineHeight='1'
    >
      {t('historique_livraison.pas_historique_achat_recent')}
    </Text>
  );

  return (
    <HaliRenderOne
      nav={itemState.follow('article_statistiques')}
      render={({data: statsData}) => {
        // Trier l'historique par date (du plus récent au plus ancien)
        const statsTriees = [...statsData.tolede.stats_par_livraison].sort(
          (a, b) =>
            new Date(b.livraison_du).getTime() -
            new Date(a.livraison_du).getTime(),
        );

        // Pas d'historique d'achat : si l'article a déjà été commandé pour la date
        // courante, on affiche le badge dédié ; sinon « Aucun historique récent »,
        // accompagné de la pastille de tendance en mode commercial.
        if (statsTriees.length === 0) {
          if (isDejaCommande) return badgeDejaCommandeSeul;
          const evolutionConfig = peutVoirEvolution
            ? getStatutEvolutionConfig(statsData.statut_evolution)
            : undefined;
          return (
            <HStack
              spacing={1}
              w='fit-content'
              h='24px'
              px='2'
              border='1px solid'
              borderColor='gray.light'
              borderRadius='6px'
            >
              {evolutionConfig && (
                <PastilleEvolution config={evolutionConfig} />
              )}
              {labelAucunHistorique}
            </HStack>
          );
        }

        // Premier (dernier achat)
        const dernierAchat = statsTriees[0];
        // 5 dernières entrées pour le tooltip
        const dernierAchats = statsTriees.slice(0, 5);

        // Mode commercial : swap clock → indicateur d'évolution selon `statut_evolution`.
        // `ras` ou statut absent → fallback sur clock (config = undefined).
        const evolutionConfig = peutVoirEvolution
          ? getStatutEvolutionConfig(statsData.statut_evolution)
          : undefined;

        const iconPrincipal = evolutionConfig ? (
          <PastilleEvolution config={evolutionConfig} />
        ) : (
          <TimeIcon
            px={0}
            pr='0px'
            boxSize={3.5}
            borderRadius='5px'
            fontWeight='300'
            color={isDejaCommande ? 'orange.warning' : undefined}
          />
        );

        // L'état « déjà commandé » est uniquement signalé par la couleur warning
        const leftIconElement = iconPrincipal;

        return (
          <Tooltip
            hasArrow={false}
            bg='white'
            color='gray.dark'
            border='1px solid'
            borderColor='gray.light'
            borderRadius='8px'
            boxShadow='md'
            p='3'
            // Les prix élargissent le tooltip
            maxW={peutVoirPrix ? 'none' : undefined}
            isOpen={isOpen}
            label={
              <VStack
                alignItems='stretch'
                spacing='2'
                minW='200px'
              >
                {/* Section « déjà commandé » : affichée en haut du tooltip si l'article
                    a déjà été commandé pour la date de livraison sélectionnée. */}
                {isDejaCommande && (
                  <>
                    <DejaCommandeTooltipContent
                      stats={statsDejaCommande}
                      unite={unite}
                      date={date_de_livraison}
                    />
                    <Divider borderColor='gray.light' />
                  </>
                )}
                <Text
                  fontSize='xs'
                  fontWeight='700'
                  color='gray.dark'
                >
                  {t('historique_livraison.derniers_achats')}
                </Text>
                <Divider borderColor='gray.light' />
                {peutVoirPrix ? (
                  // Avec les prix : colonnes alignées (date, quantité, prix, catégorie)
                  <Grid
                    templateColumns='repeat(4, auto)'
                    columnGap='4'
                    rowGap='1'
                    alignItems='center'
                  >
                    {dernierAchats.map((stat, index) => (
                      <HistoriqueAchatLigneAvecPrix
                        key={`${stat.livraison_du}-${index}`}
                        livraison_du={stat.livraison_du}
                        quantite={stat.quantite}
                        uniteDeCommande={unite}
                        prix={
                          prixParDate &&
                          (prixParDate.get(stat.livraison_du) ?? [])
                        }
                        uniteDeFacturation={itemState.data.unite_de_facturation}
                      />
                    ))}
                  </Grid>
                ) : (
                  <VStack
                    alignItems='stretch'
                    spacing='1'
                  >
                    {dernierAchats.map((stat, index) => (
                      <HStack
                        key={`${stat.livraison_du}-${index}`}
                        justify='space-between'
                      >
                        <Text
                          fontSize='xs'
                          color='gray.dark'
                        >
                          {dateAvecSlashes(stat.livraison_du)}
                        </Text>
                        <Text
                          fontSize='xs'
                          color='gray.dark'
                          fontWeight='600'
                        >
                          {stat.quantite}{' '}
                          {t(`unite.${unite}`, {count: stat.quantite})}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                )}
              </VStack>
            }
          >
            <Button
              size='xs'
              onClick={handleClick}
              iconSpacing={1}
              alignItems='center'
              leftIcon={leftIconElement}
              border='1px solid'
              borderColor={isDejaCommande ? 'yellow.50' : 'gray.light'}
              borderRadius='6px'
              background={isDejaCommande ? 'yellow.50' : 'transparent'}
              px='1'
              cursor='pointer'
              aria-label={t('historique_livraison.historique_achat')}
            >
              {/* Hover handlers portés par le Text et non par le Button : le tooltip
                  « 5 derniers achats » ne s'ouvre pas quand on survole l'icône d'évolution
                  (leftIcon), qui a son propre Tooltip. */}
              <Text
                fontSize='xs'
                color={isDejaCommande ? 'orange.warning' : 'blue.text'}
                fontWeight='500'
                lineHeight='1'
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                {dateAvecSlashes(dernierAchat.livraison_du)} :{' '}
                {dernierAchat.quantite}{' '}
                {t(`unite.${unite}`, {count: dernierAchat.quantite})}
              </Text>
            </Button>
          </Tooltip>
        );
      }}
      skeleton={() => <Skeleton style={{width: '120px', height: '20px'}} />}
      fallback={() => badgeDejaCommandeSeul ?? labelAucunHistorique}
    />
  );
};

// Mémoïsation pour éviter les rerenders inutiles
export const HistoriqueAchatButton = React.memo(HistoriqueAchatButtonComponent);
